import type { ReactNode } from 'react'
import type { HeadingSegment } from '@/components/generals/animated-heading'

import { cn } from '@/lib/utils'
import { Glow } from '@/components/generals/glow'
import { Reveal } from '@/components/generals/reveal'
import { AnimatedHeading } from '@/components/generals/animated-heading'
import { HEADING_XL } from '@/components/generals/section-heading'
import { LogoStrip } from '@/components/sections/logo-strip'

type PageHeroProps = {
    segments: HeadingSegment[]
    subtitle: string
    trustedLabel?: string
    showLogos?: boolean
    showGlow?: boolean
    headingClassName?: string
    children?: ReactNode
}

/**
 * The shared inner-page opener: word-staggered headline, supporting line, then
 * the trusted-by logo ticker, matching the About and Services pages.
 */
export const PageHero = ({
    segments,
    subtitle,
    trustedLabel = 'Trusted by some of the biggest brands:',
    showLogos = true,
    showGlow = false,
    headingClassName,
    children
}: PageHeroProps) => {
    return (
        <>
            <section
                id='hero'
                className='relative w-full overflow-hidden px-6 pt-[140px] pb-[90px] md:px-8 lg:px-16 lg:pt-[190px] lg:pb-[120px]'
            >
                {showGlow ? <Glow variant='hero' /> : null}

                <div className='relative z-1 mx-auto flex w-full max-w-[1200px] flex-col items-center gap-6'>
                    <AnimatedHeading
                        as='h1'
                        immediate
                        delay={0.15}
                        segments={segments}
                        className={cn(HEADING_XL, 'mx-auto max-w-[17ch] text-center', headingClassName)}
                    />

                    <Reveal
                        immediate
                        delay={0.45}
                        y={16}
                        blur={8}
                        duration={0.8}
                        className='mx-auto max-w-[62ch] text-center text-[clamp(1rem,1.2vw,1.125rem)] leading-[1.55] tracking-[-0.01em] text-white/56'
                    >
                        {subtitle}
                    </Reveal>

                    {children}
                </div>
            </section>

            {showLogos ? (
                <LogoStrip
                    immediate
                    delay={0.7}
                    label={trustedLabel}
                />
            ) : null}
        </>
    )
}
